import React from "react";
import Metrics, { METRIC_GAP, METRIC_GAP_Half } from "../Metrics";
import { useSelector } from "react-redux";
import {
  getDimensions,
  getLineAlignment,
  getTileShape,
} from "../../../lib/redux/slices/dimensions-slice";
import { getCanvasValues } from "../../../utils/canvas";
import { useDebounce } from "use-debounce";
import config from "../../../utils/config";

const ObjectMetrics = ({
  groupedData,
  metricRotation,
  setCanvasOffsetX,
  setCanvasOffsetY,
  setCanvasOffsetX1,
  setCanvasOffsetY1,
}) => {
  const currentShape = useSelector(getTileShape);
  const { top, left, bottom, right } = useSelector(getDimensions);
  const lineAlignment = useSelector(getLineAlignment);
  const {
    canvasTop: cTop,
    canvasLeft: cLeft,
    canvasRight: cRight,
    canvasBottom: cBottom,
    canvasTop,
    canvasLeft,
    canvasRight,
    canvasBottom,
  } = getCanvasValues({ top, left, bottom, right }, currentShape,lineAlignment);

  // const [canvasTop] = useDebounce(cTop, config.debounceTime);
  // const [canvasLeft] = useDebounce(cLeft, config.debounceTime);
  // const [canvasRight] = useDebounce(cRight, config.debounceTime);
  // const [canvasBottom] = useDebounce(cBottom, config.debounceTime);

  const rotationSortMap = {
    0: (a, b) => (a.x < b.x ? -1 : 1),
    90: (a, b) => (a.y < b.y ? -1 : 1),
    "-90": (a, b) => (a.y < b.y ? -1 : 1),
    180: (a, b) => (a.x < b.x ? -1 : 1),
    270: (a, b) => (a.y < b.y ? -1 : 1),
  };

  const data = (groupedData[metricRotation] || []).sort(
    rotationSortMap[metricRotation]
  );

  switch (true) {
    case metricRotation === 90:
      setCanvasOffsetX(METRIC_GAP_Half + METRIC_GAP_Half * (data?.length + 1));

      return data?.map((item, i) => {
        const { x, y, metricType, position } = item;

        let _y = 0;
        if (currentShape === "right") {
          if (metricType) {
            if (position === "bottomLeft") {
              _y = canvasLeft - y;
            }
          }
        }

        return (
          <Metrics
            key={i}
            x={-METRIC_GAP - METRIC_GAP_Half * i}
            y={_y}
            width={x}
            height={y}
            text={y}
            orientation={"vertical"}
            rotation={"left"}
            index={i}
            degree={90}
          />
        );
      });

    case metricRotation === -90:
      return data?.map((item, i) => {
        const { x, y, metricType, position } = item;

        return (
          <Metrics
            key={i}
            x={-METRIC_GAP - METRIC_GAP_Half * (i + 1)}
            y={0}
            width={x}
            height={y}
            text={y}
            orientation={"vertical"}
            index={i}
            degree={-90}
          />
        );
      });

    case metricRotation === 270:
      if (["left", "right"].includes(currentShape)) {
        setCanvasOffsetX1(METRIC_GAP_Half * data?.length);
      }

      return data?.map((item, i) => {
        const { x, y, metricType, position } = item;

        // let _y = currentShape === "simple" ? canvasLeft : canvasRight;
        let _x = canvasTop + METRIC_GAP_Half * i;
        let _y = 0;
        let _width = x;
        let _height = y;
        if (currentShape === "simple") {
          if (metricType) {
            if (position === "bottomLeft") {
              _y = 0;
            }
            if (position === "bottomRight") {
              _y = canvasLeft - y;
            }
          }
        }

        if (currentShape === "left") {
          if (metricType) {
            if (position === "bottomLeft") {
              _y = 0;
            }
            if (position === "bottomHalfRight") {
              _y = canvasLeft - y;
              _x = canvasBottom;
            }

            if (position === "bottomRight") {
              _y = canvasRight - y;
            }
          }
        }

        if (currentShape === "right") {
          if (metricType) {
            if (position === "bottomRight") {
              _y = canvasRight - x;
              _x = canvasTop;
            }
          }
        }

        return (
          <Metrics
            key={i}
            x={_x}
            y={_y}
            width={_width}
            height={_height}
            text={y}
            orientation={"vertical"}
            rotation={"right"}
            index={i}
            degree={270}
          />
        );
      });

    case metricRotation === 180:
      setCanvasOffsetY(METRIC_GAP_Half + METRIC_GAP_Half * (data?.length + 1));

      return data?.map((item, i) => {
        const { x, y, metricType, position } = item;
        let _x = 0;
        let _y = -METRIC_GAP - METRIC_GAP_Half * i;

        if (currentShape === "right") {
          if (metricType) {
            if (position === "topRight") {
              _y = -METRIC_GAP;
              _x = canvasTop - x;
            }
          }
        }

        return (
          <Metrics
            key={i}
            x={_x}
            y={_y}
            width={x}
            height={y}
            text={x}
            orientation={"horizontal"}
            rotation="top"
            index={i}
            degree={180}
          />
        );
      });

    case metricRotation === 0:
      setCanvasOffsetY1(METRIC_GAP_Half * data?.length);

      return data?.map((item, i) => {
        const { x, y, metricType, position } = item;

        let _yy = ["simple", "left"].includes(currentShape)
          ? canvasLeft
          : currentShape === "right"
          ? canvasRight
          : 0;

        let _y = _yy + METRIC_GAP_Half * i;

        let _x = currentShape === "right" ? canvasBottom : 0;
        let _width = currentShape === "right" ? x - canvasBottom : x;
        // const _width = x;
        let _text = currentShape === "right" ? x - canvasBottom : x;
        // const _text = x;

        if (currentShape === "simple") {
          if (metricType) {
            if (position === "bottomLeft") {
              _x = 0;
            }
            if (position === "bottomRight") {
              _x = canvasTop - x;
            }
          }
        }

        if (currentShape === "left") {
          if (metricType) {
            if (position === "bottomLeft") {
              _x = 0;
            }
            if (position === "bottomRight") {
              _x = canvasTop - x;
              _y = canvasRight;
            }

            if (position === "bottomLefttHalf") {
              _x = canvasBottom - x;
              _y = canvasLeft;
            }
          }
        }

        if (currentShape === "right") {
          if (metricType) {
            if (position === "bottomLeft") {
              _y = canvasLeft;
              _width = x;
              _x = 0;
              _text = x;
            }
            if (position === "bottomRight") {
              _y = canvasRight;
              _x = canvasBottom - x;
            }

            if (position === "bottomHalfRight") {
              _y = canvasRight;
              _x = canvasBottom;
              _width = x;
              _text = x;
            }
          }
        }

        return (
          <Metrics
            key={i}
            x={_x}
            y={_y}
            width={_width}
            height={y}
            text={_text}
            orientation={"horizontal"}
            index={i}
            rotation="bottom"
            degree={0}
          />
        );
      });

    default:
      return null;
  }
};

export default ObjectMetrics;
